/**
 * NOS-193: Donor/contact CSV import — client-side parsing, header mapping, and
 * row normalization. All functions here are pure so they can be unit-tested
 * without a DOM or network. The wizard (ImportContactsWizard.tsx) parses the CSV
 * with papaparse, maps columns to canonical contact fields with these helpers,
 * normalizes each row to an ImportContactRow, then ships chunks to the
 * `import-contacts` edge function which owns dedupe/merge/upsert against the DB.
 *
 * Schema facts this file matches exactly (contacts table):
 *   email text NOT NULL, name, phone, address_line1/2, city, state, postal_code,
 *   country (default 'US'), source CHECK includes 'import',
 *   total_donated integer (cents), donation_count integer,
 *   first_donated_at/last_donated_at timestamptz, is_recurring_donor boolean,
 *   metadata jsonb. Tags land in donor_tags (contact_id, org_id, tag).
 */

// Canonical contact fields a CSV column can map to.
export type ContactField =
  | "name"
  | "first_name"
  | "last_name"
  | "email"
  | "phone"
  | "address_line1"
  | "address_line2"
  | "city"
  | "state"
  | "postal_code"
  | "country"
  | "tags"
  | "total_donated"
  | "donation_count"
  | "first_donated_at"
  | "last_donated_at"
  | "is_recurring_donor"

// A CSV column can map to a canonical field, be ignored, or fall through to
// contacts.metadata (jsonb) so no source data is silently dropped.
export type MappingTarget = ContactField | "ignore" | "metadata"

// Header → target. Keys are the exact CSV header strings.
export type ColumnMapping = Record<string, MappingTarget>

// UI-facing labels for the manual remap dropdown, in a sensible display order.
export const CONTACT_FIELD_OPTIONS: { value: ContactField; label: string }[] = [
  { value: "name", label: "Full name" },
  { value: "first_name", label: "First name" },
  { value: "last_name", label: "Last name" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "address_line1", label: "Address line 1" },
  { value: "address_line2", label: "Address line 2" },
  { value: "city", label: "City" },
  { value: "state", label: "State / Province" },
  { value: "postal_code", label: "ZIP / Postal code" },
  { value: "country", label: "Country" },
  { value: "tags", label: "Tags" },
  { value: "total_donated", label: "Total donated ($)" },
  { value: "donation_count", label: "Gift count" },
  { value: "first_donated_at", label: "First gift date" },
  { value: "last_donated_at", label: "Last gift date" },
  { value: "is_recurring_donor", label: "Recurring donor" },
]

/** Normalize a header for alias matching: lowercase, alphanumerics only. */
export function normalizeHeader(header: string): string {
  return header.toLowerCase().replace(/[^a-z0-9]+/g, "")
}

// Ordered alias table. First field whose alias set contains the normalized
// header wins. Matching is exact-equality on the normalized string, so
// "firstname" never collides with "name".
const ALIAS_TABLE: [ContactField, string[]][] = [
  ["email", ["email", "emailaddress", "primaryemail", "donoremail", "contactemail", "e"]],
  ["first_name", ["firstname", "fname", "givenname", "first"]],
  ["last_name", ["lastname", "lname", "surname", "familyname", "last"]],
  [
    "name",
    ["name", "fullname", "donorname", "contactname", "displayname", "supportername", "membername"],
  ],
  ["phone", ["phone", "phonenumber", "mobile", "mobilephone", "cell", "cellphone", "telephone", "primaryphone"]],
  [
    "address_line1",
    ["address", "address1", "addressline1", "streetaddress", "street", "addressline", "mailingaddress"],
  ],
  ["address_line2", ["address2", "addressline2", "apt", "apartment", "suite", "unit"]],
  ["city", ["city", "town"]],
  ["state", ["state", "province", "region", "stateprovince"]],
  ["postal_code", ["zip", "zipcode", "postalcode", "postcode", "postal"]],
  ["country", ["country", "countrycode"]],
  ["tags", ["tags", "tag", "labels", "label", "groups", "group", "segments", "segment"]],
  [
    "total_donated",
    [
      "totaldonated",
      "totaldonations",
      "totalcontributions",
      "totalcontributed",
      "totalgiven",
      "totalamount",
      "totalraised",
      "lifetimegiving",
      "lifetimeamount",
      "totalgifts",
      "totalgiftamount",
      "amount",
      "amountdonated",
    ],
  ],
  [
    "donation_count",
    [
      "donationcount",
      "numberofdonations",
      "numberofcontributions",
      "giftcount",
      "numberofgifts",
      "numdonations",
      "donations",
      "contributions",
      "transactions",
      "gifts",
    ],
  ],
  [
    "first_donated_at",
    ["firstdonation", "firstdonationdate", "firstgift", "firstgiftdate", "firstdonated", "firstcontribution"],
  ],
  [
    "last_donated_at",
    ["lastdonation", "lastdonationdate", "lastgift", "lastgiftdate", "lastdonated", "lastcontribution", "recentdonation"],
  ],
  [
    "is_recurring_donor",
    ["recurring", "recurringdonor", "isrecurring", "monthlydonor", "recurringgiver", "recurringflag"],
  ],
]

/** Auto-detect the canonical field for a single CSV header, or null. */
export function detectField(header: string): ContactField | null {
  const norm = normalizeHeader(header)
  if (!norm) return null
  for (const [field, aliases] of ALIAS_TABLE) {
    if (aliases.includes(norm)) return field
  }
  return null
}

// Built-in presets. Each is a named dictionary of normalized header → field for
// a known export format. Presets are convenience only — the alias auto-detect
// already covers most of these; they let a user force a known layout and are
// cheap to extend. Keys are normalized (see normalizeHeader).
export type PresetName = "auto" | "givebutter" | "donorbox" | "excel"

export const PRESET_LABELS: Record<PresetName, string> = {
  auto: "Auto-detect",
  givebutter: "Givebutter export",
  donorbox: "Donorbox export",
  excel: "Generic Excel / spreadsheet",
}

export const PRESETS: Record<Exclude<PresetName, "auto">, Record<string, ContactField>> = {
  givebutter: {
    firstname: "first_name",
    lastname: "last_name",
    email: "email",
    phone: "phone",
    addressline1: "address_line1",
    addressline2: "address_line2",
    city: "city",
    state: "state",
    zipcode: "postal_code",
    zip: "postal_code",
    country: "country",
    tags: "tags",
    totalcontributions: "total_donated",
    totalraised: "total_donated",
    numberofcontributions: "donation_count",
    firstcontribution: "first_donated_at",
    lastcontribution: "last_donated_at",
    recurring: "is_recurring_donor",
  },
  donorbox: {
    name: "name",
    donorname: "name",
    email: "email",
    phone: "phone",
    address: "address_line1",
    city: "city",
    state: "state",
    zipcode: "postal_code",
    country: "country",
    totaldonations: "total_donated",
    totalamount: "total_donated",
    numberofdonations: "donation_count",
    donationscount: "donation_count",
    firstdonationdate: "first_donated_at",
    lastdonationdate: "last_donated_at",
    recurringdonor: "is_recurring_donor",
    campaign: "tags",
  },
  excel: {
    name: "name",
    fullname: "name",
    firstname: "first_name",
    lastname: "last_name",
    email: "email",
    phone: "phone",
    address: "address_line1",
    city: "city",
    state: "state",
    zip: "postal_code",
    tags: "tags",
    totalgiven: "total_donated",
    giftcount: "donation_count",
    firstgift: "first_donated_at",
    lastgift: "last_donated_at",
  },
}

/**
 * Build an initial column mapping for a set of headers under a preset.
 * Preset overrides win; otherwise auto-detect; otherwise the column drops into
 * contacts.metadata so nothing is lost.
 */
export function buildMapping(headers: string[], preset: PresetName = "auto"): ColumnMapping {
  const dict = preset === "auto" ? null : PRESETS[preset]
  const mapping: ColumnMapping = {}
  for (const header of headers) {
    const norm = normalizeHeader(header)
    const fromPreset = dict?.[norm]
    if (fromPreset) {
      mapping[header] = fromPreset
      continue
    }
    const detected = detectField(header)
    mapping[header] = detected ?? "metadata"
  }
  return mapping
}

// ---------------------------------------------------------------------------
// Value parsers
// ---------------------------------------------------------------------------

/**
 * Parse a money string ("$1,234.56", "1234.56", "(45.00)", "€10") to integer
 * cents. Returns null for empty; returns null (a signal to flag the row) for
 * non-empty-but-unparseable input.
 */
export function parseMoneyToCents(raw: string | null | undefined): number | null {
  if (raw == null) return null
  let s = String(raw).trim()
  if (!s) return null
  let negative = false
  // Accounting-style negatives: (123.45)
  if (/^\(.*\)$/.test(s)) {
    negative = true
    s = s.slice(1, -1)
  }
  s = s.replace(/[$£€,\s]/g, "").replace(/usd/gi, "")
  if (s.startsWith("-")) {
    negative = true
    s = s.slice(1)
  }
  if (!/^(\d+(\.\d+)?|\.\d+)$/.test(s)) return null
  const num = Number(s)
  if (!isFinite(num)) return null
  const cents = Math.round(num * 100)
  return negative ? -cents : cents
}

/** Parse an integer count ("1,024", "12"). Empty → null; invalid → null. */
export function parseIntCount(raw: string | null | undefined): number | null {
  if (raw == null) return null
  const s = String(raw).trim().replace(/[,\s]/g, "")
  if (!s) return null
  if (!/^-?\d+$/.test(s)) return null
  const n = parseInt(s, 10)
  if (!isFinite(n) || n < 0) return null
  return n
}

function buildISO(year: number, month: number, day: number): string | null {
  if (month < 1 || month > 12 || day < 1 || day > 31) return null
  const d = new Date(Date.UTC(year, month - 1, day))
  // Reject overflow (e.g. Feb 30 rolling into March).
  if (d.getUTCFullYear() !== year || d.getUTCMonth() !== month - 1 || d.getUTCDate() !== day) {
    return null
  }
  return d.toISOString()
}

/**
 * Parse common US date formats to an ISO timestamp string (UTC midnight).
 * Handles MM/DD/YYYY, M/D/YY, MM-DD-YYYY, YYYY-MM-DD, YYYY/MM/DD, and named
 * months ("May 1, 2023") via Date.parse. Empty → null; unparseable → null.
 */
export function parseUSDate(raw: string | null | undefined): string | null {
  if (raw == null) return null
  const s = String(raw).trim()
  if (!s) return null

  // ISO-ish: YYYY-MM-DD or YYYY/MM/DD
  let m = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/)
  if (m) return buildISO(Number(m[1]), Number(m[2]), Number(m[3]))

  // US: M/D/Y or M-D-Y (2- or 4-digit year)
  m = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{2,4})$/)
  if (m) {
    let year = Number(m[3])
    if (m[3].length === 2) year += year < 50 ? 2000 : 1900
    return buildISO(year, Number(m[1]), Number(m[2]))
  }

  // Named month formats: "May 1, 2023", "1 May 2023", ISO datetimes, etc.
  const t = Date.parse(s)
  if (!isNaN(t)) return new Date(t).toISOString()

  return null
}

const TRUTHY = new Set(["true", "yes", "y", "1", "recurring", "monthly", "active", "t"])

/** Parse a recurring/boolean flag. Empty or unrecognized → false. */
export function parseBooleanFlag(raw: string | null | undefined): boolean {
  if (raw == null) return false
  return TRUTHY.has(String(raw).trim().toLowerCase())
}

/** Split a tags cell on comma/semicolon/pipe, trim, drop empties, dedupe. */
export function parseTags(raw: string | null | undefined): string[] {
  if (raw == null) return []
  const parts = String(raw)
    .split(/[,;|]/)
    .map((t) => t.trim())
    .filter(Boolean)
  return Array.from(new Set(parts))
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** True if the string is a plausibly valid email. */
export function isValidEmail(email: string): boolean {
  return EMAIL_RE.test(email)
}

/** Small deterministic string hash → base36, for synthetic no-email keys. */
export function hashString(s: string): string {
  let h = 0
  for (let i = 0; i < s.length; i++) {
    h = (Math.imul(h, 31) + s.charCodeAt(i)) | 0
  }
  return (h >>> 0).toString(36)
}

// ---------------------------------------------------------------------------
// Row normalization
// ---------------------------------------------------------------------------

/** A parsed, DB-ready contact row shipped to the import-contacts edge function. */
export interface ImportContactRow {
  email: string
  name: string | null
  phone: string | null
  address_line1: string | null
  address_line2: string | null
  city: string | null
  state: string | null
  postal_code: string | null
  country: string | null
  total_donated: number
  donation_count: number
  first_donated_at: string | null
  last_donated_at: string | null
  is_recurring_donor: boolean
  tags: string[]
  metadata: Record<string, string>
  /** True when email was synthesized because the source row had none. */
  no_email: boolean
  /** 1-based source row number, for the skipped-rows report. */
  source_row: number
}

export interface SkippedRow {
  row: number
  email: string
  reason: string
}

export interface NormalizeResult {
  valid: ImportContactRow[]
  skipped: SkippedRow[]
  /** Count of valid rows whose email was synthesized (no email in source). */
  noEmailCount: number
}

type RawRow = Record<string, string | null | undefined>

/**
 * Normalize one parsed CSV row into an ImportContactRow, or a skip reason.
 * Rules:
 *  - Unmapped/"metadata" columns with a value land in metadata.
 *  - name is composed from first_name + last_name when no full name is mapped.
 *  - A present-but-invalid email or a present-but-unparseable money/date cell
 *    skips the whole row with a reason (never aborts the batch).
 *  - A missing email is allowed: email is synthesized from name/phone so the
 *    row is importable and idempotent, and flagged (no_email + metadata marker).
 *  - A row with no email AND no name AND no phone is skipped as empty.
 */
export function normalizeRow(
  row: RawRow,
  mapping: ColumnMapping,
  rowNumber: number
): { ok: true; contact: ImportContactRow } | { ok: false; skip: SkippedRow } {
  const fields: Partial<Record<ContactField, string>> = {}
  const tags: string[] = []
  const metadata: Record<string, string> = {}

  for (const [header, target] of Object.entries(mapping)) {
    const rawVal = row[header]
    const value = rawVal == null ? "" : String(rawVal).trim()
    if (target === "ignore") continue
    if (target === "metadata") {
      if (value) metadata[header] = value
      continue
    }
    if (target === "tags") {
      for (const t of parseTags(value)) if (!tags.includes(t)) tags.push(t)
      continue
    }
    // First non-empty value wins when multiple columns map to the same field.
    if (value && fields[target] == null) fields[target] = value
  }

  // Compose name from first/last when no full name provided.
  let name = fields.name ?? null
  if (!name) {
    const composed = [fields.first_name, fields.last_name].filter(Boolean).join(" ").trim()
    name = composed || null
  }

  const phone = fields.phone ?? null

  // Email handling.
  let email = (fields.email ?? "").toLowerCase().trim()
  let noEmail = false
  if (email) {
    if (!isValidEmail(email)) {
      return { ok: false, skip: { row: rowNumber, email, reason: `Invalid email format: "${email}"` } }
    }
  } else {
    // No email: importable but flagged, IF we have something to identify them.
    if (!name && !phone) {
      return { ok: false, skip: { row: rowNumber, email: "", reason: "Empty row (no email, name, or phone)" } }
    }
    noEmail = true
    email = `noemail-${hashString(`${name ?? ""}|${phone ?? ""}`)}@import.local`
    metadata._imported_without_email = "true"
  }

  // Money.
  let total_donated = 0
  const rawMoney = fields.total_donated
  if (rawMoney != null) {
    const cents = parseMoneyToCents(rawMoney)
    if (cents == null) {
      return { ok: false, skip: { row: rowNumber, email, reason: `Invalid amount: "${rawMoney}"` } }
    }
    total_donated = cents
  }

  // Gift count.
  let donation_count = 0
  const rawCount = fields.donation_count
  if (rawCount != null) {
    const n = parseIntCount(rawCount)
    if (n == null) {
      return { ok: false, skip: { row: rowNumber, email, reason: `Invalid gift count: "${rawCount}"` } }
    }
    donation_count = n
  }

  // Dates.
  let first_donated_at: string | null = null
  if (fields.first_donated_at != null) {
    const d = parseUSDate(fields.first_donated_at)
    if (d == null) {
      return { ok: false, skip: { row: rowNumber, email, reason: `Invalid first gift date: "${fields.first_donated_at}"` } }
    }
    first_donated_at = d
  }
  let last_donated_at: string | null = null
  if (fields.last_donated_at != null) {
    const d = parseUSDate(fields.last_donated_at)
    if (d == null) {
      return { ok: false, skip: { row: rowNumber, email, reason: `Invalid last gift date: "${fields.last_donated_at}"` } }
    }
    last_donated_at = d
  }

  const contact: ImportContactRow = {
    email,
    name,
    phone,
    address_line1: fields.address_line1 ?? null,
    address_line2: fields.address_line2 ?? null,
    city: fields.city ?? null,
    state: fields.state ?? null,
    postal_code: fields.postal_code ?? null,
    country: fields.country ?? null,
    total_donated,
    donation_count,
    first_donated_at,
    last_donated_at,
    is_recurring_donor: fields.is_recurring_donor != null ? parseBooleanFlag(fields.is_recurring_donor) : false,
    tags,
    metadata,
    no_email: noEmail,
    source_row: rowNumber,
  }
  return { ok: true, contact }
}

/**
 * Normalize an array of parsed CSV rows. Row numbers are 1-based over the input
 * (matching the spreadsheet body, i.e. excluding the header line).
 */
export function normalizeRows(rows: RawRow[], mapping: ColumnMapping): NormalizeResult {
  const valid: ImportContactRow[] = []
  const skipped: SkippedRow[] = []
  let noEmailCount = 0
  rows.forEach((row, i) => {
    const result = normalizeRow(row, mapping, i + 1)
    if (result.ok) {
      valid.push(result.contact)
      if (result.contact.no_email) noEmailCount++
    } else {
      skipped.push(result.skip)
    }
  })
  return { valid, skipped, noEmailCount }
}

/** Serialize skipped rows to a downloadable CSV string. */
export function skippedRowsToCSV(skipped: SkippedRow[]): string {
  const esc = (v: string) =>
    /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v
  const lines = ["Row,Email,Reason"]
  for (const s of skipped) {
    lines.push([String(s.row), esc(s.email), esc(s.reason)].join(","))
  }
  return lines.join("\n")
}

/** True when the mapping has at least one column feeding the email field. */
export function mappingHasEmail(mapping: ColumnMapping): boolean {
  return Object.values(mapping).includes("email")
}
